import {
  OrgRoles,
  postUsersInvitationsCreateMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Input,
  Select,
  toast,
} from "@win-reality/react-ui";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { Form } from "@/components/ui/form";
import { Screen, ScreenManager } from "@/components/ui/screen-manager";
import { useAppContext } from "@/hooks/use-app-context";
import { useClipboard } from "@/hooks/use-clipboard";
import { addOrgPrefix } from "@/lib/utils";
import {
  OrganizationRoles,
  RoleOptions,
  roleOptionToOrgRolesMapping,
} from "@/types/roles";

import { getAvailableRoleOptions } from "../handlers/getAvailableRoleOptions";

export interface CreateRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasAccountOwner: boolean;
  organizationId: string;
}

interface FormSchema {
  role: OrganizationRoles;
}

export default function CreateRoleModal({
  open,
  onOpenChange,
  hasAccountOwner,
  organizationId,
}: CreateRoleModalProps) {
  const { currentOrganization, user } = useAppContext();
  const createInvitation = useMutation({
    ...postUsersInvitationsCreateMutation(),
    onError: () => {
      toast("Failed to generate link", { variant: "error" });
    },
  });

  const [generatedLink, setGeneratedLink] = useState("");
  const [loading, setLoading] = useState(false);
  const { hasCopied, onCopy } = useClipboard(generatedLink);

  const availableRoles = useMemo(() => {
    return getAvailableRoleOptions(
      [
        ...((user?.roles as OrgRoles[]) || []),
        ...((currentOrganization?.orgRoles as OrgRoles[]) || []),
      ],
      hasAccountOwner,
    );
  }, [user?.roles, currentOrganization?.orgRoles, hasAccountOwner]);

  const initialRole = useMemo(
    () =>
      !availableRoles?.some((role) => role.value === "staff")
        ? "tagger"
        : "staff",
    [availableRoles],
  );

  const formMethods = useForm<FormSchema>({
    defaultValues: {
      role: initialRole,
    },
  });

  const fetchInviteLink = async (data: {
    role: OrganizationRoles;
  }): Promise<string> => {
    const response = await createInvitation.mutateAsync({
      body: {
        roles: [...roleOptionToOrgRolesMapping[data.role]],
        duration: "forever",
        orgId: addOrgPrefix(organizationId),
      },
    });
    const currentBaseUrl = window.location.origin;
    return `${currentBaseUrl}/claim-permissions/?token=${response.invitationCode}`;
  };

  const handleGenerateLink = async (data: FormSchema) => {
    setLoading(true);
    try {
      const url = await fetchInviteLink(data);
      setGeneratedLink(url);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      formMethods.reset({ role: initialRole });
      setGeneratedLink("");
    }
  }, [open, formMethods, initialRole]);

  const selectedRole = formMethods.watch("role");
  const modalTitle = useMemo(() => {
    return `Create ${RoleOptions[selectedRole as keyof typeof RoleOptions]}`;
  }, [selectedRole]);

  const roleItems = useMemo(
    () =>
      availableRoles?.map((option) => ({
        label: option.label,
        value: option.value,
      })) ?? [],
    [availableRoles],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onOpenChange(false)}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <ScreenManager defaultScreen="form">
          <Screen screenName="form">
            {({ setCurrentScreen }) => (
              <>
                <DialogScrollableContent
                  setScrollContainer={setScrollContainer}
                >
                  <DialogHeader>
                    <DialogTitle>{modalTitle}</DialogTitle>
                  </DialogHeader>

                  <Form {...formMethods}>
                    <form
                      id="create-role-form"
                      onSubmit={formMethods.handleSubmit(async (data) => {
                        await handleGenerateLink({
                          role: data.role as OrganizationRoles,
                        });
                        setCurrentScreen("link");
                      })}
                    >
                      <div className="grid gap-4 py-4">
                        <Select
                          data-testid="roles"
                          items={roleItems}
                          value={formMethods.watch("role")}
                          onValueChange={(value) =>
                            formMethods.setValue(
                              "role",
                              value as OrganizationRoles,
                            )
                          }
                          placeholder="Select a role"
                        />
                      </div>
                    </form>
                  </Form>
                </DialogScrollableContent>

                <DialogFooter showDivider={showDivider}>
                  <div className="flex w-full justify-between">
                    <Button
                      variant="ghost"
                      data-testid="cancel-button"
                      onClick={() => onOpenChange(false)}
                      className="underline"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="secondary-flat-light"
                      type="submit"
                      form="create-role-form"
                      data-testid="generate-link"
                      isLoading={loading}
                    >
                      Generate Link
                    </Button>
                  </div>
                </DialogFooter>
              </>
            )}
          </Screen>
          <Screen screenName="link">
            <>
              <DialogScrollableContent setScrollContainer={setScrollContainer}>
                <DialogHeader>
                  <DialogTitle>{modalTitle}</DialogTitle>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                  <div className="flex gap-2">
                    <Input value={generatedLink} readOnly />
                    <Button
                      variant="secondary-flat-light"
                      onClick={onCopy}
                      data-testid="copy-link"
                    >
                      {hasCopied ? "Copied!" : "Copy Link"}
                    </Button>
                  </div>
                </div>
              </DialogScrollableContent>

              <DialogFooter showDivider={showDivider}>
                <div className="flex w-full justify-center">
                  <Button
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                    className="underline"
                  >
                    Done
                  </Button>
                </div>
              </DialogFooter>
            </>
          </Screen>
        </ScreenManager>
      )}
    </Dialog>
  );
}
